import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { RELEASE_ARTWORK_PX, releaseArtworkUrl } from './aiReel/releaseArtworkUrl';
import { daysToRelease } from './studioRules';
import { studioMessages } from './studioMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StudioSongRowProps {
  item: CatalogItem;
  /** The picker: the whole row picks the song. */
  onPick?: () => void;
  /** The Studio head (l1): "Change song" on the right. */
  onChange?: () => void;
  /** The theme chip of the song (design q3a), before "Change song". Below 700 px it wraps to its own line. */
  themeChip?: React.ReactNode;
}

/**
 * One song (design l1): the artwork, the title and the song files line "MARA VALE · MASTER ✓ ·
 * LYRICS · OUT IN 9 DAYS".
 */
const StudioSongRowComponent: React.FC<StudioSongRowProps> = ({ item, onPick, onChange, themeChip }) => {
  const intl = useIntl();
  const artwork = releaseArtworkUrl(item, RELEASE_ARTWORK_PX.thumb);

  const line = useMemo(() => {
    const parts = [
      item.artistName,
      intl.formatMessage(
        item.song.masterStatus === 'ready' ? studioMessages.songMasterReady : studioMessages.songNoMaster
      ),
    ];
    if (item.song.hasLyrics) {
      parts.push(intl.formatMessage(studioMessages.songLyrics));
    }
    const days = daysToRelease(item.releaseDate, new Date());
    if (days === 0) {
      parts.push(intl.formatMessage(studioMessages.songOutToday));
    } else if (days !== null && days > 0) {
      parts.push(intl.formatMessage(studioMessages.songOutIn, { days }));
    }
    return parts.join(' · ');
  }, [intl, item]);

  const body = (
    <>
      <span className={styles.songArt} aria-hidden="true">
        {artwork === undefined ? item.title.slice(0, 2).toUpperCase() : <img src={artwork} alt="" />}
      </span>
      <span className={styles.songText}>
        <span className={styles.songTitle}>{item.title}</span>
        <span className={styles.mono}>{line}</span>
      </span>
    </>
  );

  if (onPick !== undefined) {
    return (
      <button
        type="button"
        className={`${styles.songRow} btn btn-link text-start text-decoration-none`}
        onClick={onPick}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={styles.songRow}>
      {body}
      {themeChip !== undefined && <span className={styles.songChip}>{themeChip}</span>}
      {onChange !== undefined && (
        <SMLButton outline omitCap size="sm" onClick={onChange}>
          {intl.formatMessage(studioMessages.changeSong)}
        </SMLButton>
      )}
    </div>
  );
};

export const StudioSongRow = TypedMemo(StudioSongRowComponent);
